import { SelectProps, UploadFile, UploadProps, Steps, Form, Input, Checkbox, Radio, Button, Upload, Modal, Select, DatePicker, Space, Divider, Row as AntRow, Col } from "antd";
import { CheckboxValueType } from "antd/lib/checkbox/Group";
import TextArea from "antd/lib/input/TextArea";
import { RcFile } from "antd/lib/upload";
import { useState, useEffect } from "react";
import { useSelectorRoot, useDispatchRoot } from "../../redux/store";
import { editAuthorRequest, editBookRequest, editTranslatorRequest, getAuthorRequest, getCategoryRequest, getPublisherRequest } from "../../redux/controller";
import { API_URL } from "../../enums/api.enums";
import { Option } from "antd/lib/mentions";
import { ITranslator } from "../../common/translator.interface";
import { 
    UserOutlined, 
    FileTextOutlined, 
    EditOutlined, 
    CloseOutlined,
    CalendarOutlined
} from "@ant-design/icons";
import { confirmCloseModal } from "../modalEditBook/CModalConfirmClose";
import './CModal.styles.scss'
import moment from "moment";
import axios from "axios";

interface MyProps {
    open: boolean;
    data?: ITranslator;
    setOpenModalEdit: React.Dispatch<React.SetStateAction<boolean>>,
}

type LayoutType = Parameters<typeof Form>[0]['layout'];


const CModalEditTranslator = (props: MyProps) => {

    const [formLayout, setFormLayout] = useState<LayoutType>('vertical');
    const dispatch = useDispatchRoot();
    const [form] = Form.useForm();
    const [windowSize, setWindowSize] = useState([
        window.innerWidth,
        window.innerHeight,
    ]);
    const [isLoading, setIsLoading] = useState(false);
    const [hasChanges, setHasChanges] = useState(false);
    const values = Form.useWatch([], form);

    useEffect(() => {
        const handleWindowResize = () => {
            setWindowSize([window.innerWidth, window.innerHeight]);
        };

        window.addEventListener("resize", handleWindowResize);
        return () => {
            window.removeEventListener("resize", handleWindowResize);
        };
    });

    useEffect(() => {
        if (props.data) {
            form.setFieldsValue({
                name: props.data.name || getTranslatorFullName(props.data),
                description: props.data.description || '',
                birthYear: props.data.birthYear?.toString() || '',
            });
        }
    }, [props.data, form]);

    // Helper function để tạo tên đầy đủ từ firstName, lastName, middleName (tương thích ngược)
    const getTranslatorFullName = (translator: ITranslator) => {
        if (translator.name) return translator.name;
        
        let fullName = '';
        if (translator.firstName) fullName += translator.firstName;
        if (translator.middleName) fullName += (fullName ? ' ' : '') + translator.middleName;
        if (translator.lastName) fullName += (fullName ? ' ' : '') + translator.lastName;
        return fullName;
    };

    useEffect(() => {
        const currentValues = form.getFieldsValue();
        const originalName = props.data?.name || getTranslatorFullName(props.data || {} as ITranslator);
        const originalDescription = props.data?.description || '';
        const originalBirthYear = props.data?.birthYear?.toString() || '';
        
        const hasFormChanges = 
            currentValues.name !== originalName ||
            currentValues.description !== originalDescription ||
            currentValues.birthYear !== originalBirthYear;
        
        setHasChanges(hasFormChanges);
    }, [values, props.data]);

    const handleEditTranslator = async (value: any) => {
        setIsLoading(true);
        try {
            console.log("value", value);

            const bodyRequest = {
                id: props.data?.id,
                name: value.name,
                description: value.description,
                birthYear: value.birthYear ? parseInt(value.birthYear) : undefined,
            }

            await dispatch(editTranslatorRequest(bodyRequest));
            props.setOpenModalEdit(false);
        } catch (error) {
            console.error('Error updating translator:', error);
        } finally {
            setIsLoading(false);
        }
    };

    const handleCancelEditTranslator = () => {
        confirmCloseModal({
            hasChanges,
            onConfirm: () => {
                resetForm();
                props.setOpenModalEdit(false);
            },
            title: 'Xác nhận đóng',
            content: 'Bạn có thay đổi chưa lưu. Bạn có chắc chắn muốn đóng modal chỉnh sửa dịch giả?'
        });
    };

    const resetForm = () => {
        setHasChanges(false);
        form.resetFields();
    };

    return (
        <Modal
            open={props.open}
            onCancel={handleCancelEditTranslator}
            title={
                <Space>
                    <EditOutlined />
                    <span>Chỉnh sửa dịch giả</span>
                </Space>
            }
            className="translator-edit-modal"
            footer={null}
            width={600}
            destroyOnClose
            maskClosable={true}
        >
            <div className="modal-content">
                <Form
                    initialValues={props.data}
                    form={form}
                    className="translator-form"
                    layout="vertical"
                    onFinish={handleEditTranslator}
                >
                    <AntRow gutter={[16, 12]}>
                        <Col span={24}>
                            <Form.Item
                                label={
                                    <Space>
                                        <UserOutlined style={{ fontSize: '12px', color: 'var(--primary-color)' }} />
                                        <span>Tên đầy đủ dịch giả</span>
                                    </Space>
                                }
                                name="name"
                                rules={[{ required: true, message: 'Tên dịch giả không được để trống!' }]}
                            >
                                <Input
                                    placeholder="Nhập tên đầy đủ dịch giả (ví dụ: Dương Tường)"
                                    className="form-input"
                                />
                            </Form.Item>
                        </Col>

                        <Col span={12}>
                            <Form.Item
                                label={
                                    <Space>
                                        <CalendarOutlined style={{ fontSize: '12px', color: 'var(--primary-color)' }} />
                                        <span>Năm sinh</span>
                                    </Space>
                                }
                                name="birthYear"
                                rules={[
                                    { 
                                        pattern: /^\d{4}$/, 
                                        message: "Năm sinh phải là 4 chữ số!" 
                                    },
                                    {
                                        validator: (_, value) => {
                                            if (!value) return Promise.resolve();
                                            const year = parseInt(value);
                                            if (year < 1900 || year > new Date().getFullYear()) {
                                                return Promise.reject(new Error('Năm sinh phải từ 1900 đến hiện tại!'));
                                            }
                                            return Promise.resolve();
                                        }
                                    }
                                ]}
                            >
                                <Input
                                    placeholder="Nhập năm sinh (ví dụ: 1970)"
                                    className="form-input"
                                />
                            </Form.Item>
                        </Col>

                        <Col span={12}>
                            <div style={{ height: '36px' }}></div>
                        </Col>

                        <Col span={24}>
                            <Form.Item
                                label={
                                    <Space>
                                        <FileTextOutlined style={{ fontSize: '12px', color: 'var(--primary-color)' }} />
                                        <span>Mô tả</span>
                                    </Space>
                                }
                                name="description"
                            >
                                <TextArea
                                    placeholder="Nhập mô tả"
                                    rows={4}
                                    className="form-textarea"
                                />
                            </Form.Item>
                        </Col>
                    </AntRow>

                    <Divider style={{ margin: '16px 0' }} />

                    <div className="modal-actions">
                        <Button
                            onClick={handleCancelEditTranslator}
                            className="cancel-button"
                            icon={<CloseOutlined />}
                        >
                            Hủy
                        </Button>
                        <Button
                            type="primary"
                            htmlType="submit"
                            className="save-button"
                            icon={<EditOutlined />}
                            loading={isLoading}
                        >
                            Cập nhật
                        </Button>
                    </div>
                </Form>
            </div>
        </Modal>
    );
};

export default CModalEditTranslator;
